import { CURRENCY, CURRENCY_DECIMALS, type Locale } from "@salesboard/contracts";
import { getActiveLocale } from "@/i18n/active-locale";
import { translator } from "@/i18n";

/**
 * All formatting goes through here, from the shared constants. Never format inline —
 * a hand-rolled date string is how the export and the board start disagreeing.
 *
 * **Every function reads the session user's locale**, so an amount and a date look the way the
 * reader expects them to:
 *
 * | | `cs` | `en` |
 * |---|---|---|
 * | amount | `1 234 567,89 Kč` | `CZK 1,234,567.89` |
 * | date | `11. 8. 2026` | `11 Aug 2026` |
 * | quarter | `Q3 2026` | `Q3 2026` |
 *
 * The quarter is the same in both on purpose: `Q3 2026` is how the board's own column headers
 * are written, and a "3. kvartál 2026" on the detail beside a `Q3` on the grid would be two
 * spellings of one fact.
 *
 * The locale arrives from `i18n/active-locale`, which the provider sets once per render —
 * **not** as a parameter every call site has to remember, because these are called from column
 * renderers, accessible labels and toast builders as well as from components. Each function
 * still *accepts* a locale, which is how the tests assert both languages.
 *
 * What does **not** follow the reader is collation: `COLLATION_LOCALE` in the contracts package
 * fixes the board's tie-break sort, because two people in one meeting must see one card order.
 */

/** BCP 47 tags. The bare language subtag would give `cs` the wrong grouping separator. */
const INTL_LOCALES: Record<Locale, string> = { cs: "cs-CZ", en: "en-GB" };

/**
 * Formatters are expensive to construct and there are only two locales, so each one is built
 * at most once per locale and kept.
 */
function memo<T>(build: (locale: Locale) => T): (locale: Locale) => T {
  const cache = new Map<Locale, T>();
  return (locale) => {
    const existing = cache.get(locale);
    if (existing) return existing;
    const created = build(locale);
    cache.set(locale, created);
    return created;
  };
}

const currencyFormatter = memo(
  (locale) =>
    new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: "currency",
      currency: CURRENCY,
      minimumFractionDigits: CURRENCY_DECIMALS,
      maximumFractionDigits: CURRENCY_DECIMALS,
    }),
);

const compactCurrencyFormatter = memo(
  (locale) =>
    new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: "currency",
      currency: CURRENCY,
      notation: "compact",
      maximumFractionDigits: 1,
    }),
);

const numberFormatter = memo((locale) => new Intl.NumberFormat(INTL_LOCALES[locale]));

/**
 * Dates.
 *
 * `cs` takes the Czech numeric form — `11. 8. 2026`, which is what a Czech reader writes — and
 * `en` keeps the abbreviated-month shape it shipped with, because an all-numeric `en` date is
 * ambiguous between the British and American readings.
 */
const dateFormatter = memo((locale) =>
  locale === "cs"
    ? new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" })
    : new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
);

const dateTimeFormatter = memo((locale) =>
  locale === "cs"
    ? new Intl.DateTimeFormat("cs-CZ", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
);

/** The clock alone, for a timestamp whose day is being said in words. */
const timeFormatter = memo(
  (locale) => new Intl.DateTimeFormat(INTL_LOCALES[locale], { hour: "2-digit", minute: "2-digit" }),
);

/** Amounts arrive from the API as decimal strings, never as floats. */
export function formatCurrency(amount: string | number, locale: Locale = getActiveLocale()): string {
  const value = typeof amount === "string" ? Number(amount) : amount;
  return Number.isFinite(value) ? currencyFormatter(locale).format(value) : "—";
}

/** For cards and totals, where width matters more than the last koruna. */
export function formatCurrencyCompact(amount: string | number, locale: Locale = getActiveLocale()): string {
  const value = typeof amount === "string" ? Number(amount) : amount;
  return Number.isFinite(value) ? compactCurrencyFormatter(locale).format(value) : "—";
}

/** A plain count, grouped the reader's way — `1 412` in Czech, `1,412` in English. */
export function formatNumber(value: number, locale: Locale = getActiveLocale()): string {
  return Number.isFinite(value) ? numberFormatter(locale).format(value) : "—";
}

/**
 * Business dates arrive as `yyyy-MM-dd` — a calendar date with no time and no offset.
 * Parsed as UTC noon so that no display timezone can shift it onto a different day,
 * which for a date that determines an opportunity's quarter would be a real defect.
 */
export function formatDate(date: string | null | undefined, locale: Locale = getActiveLocale()): string {
  if (!date) return "—";
  const parsed = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? "—" : dateFormatter(locale).format(parsed);
}

/** Timestamps are instants and are shown in the viewer's own timezone. */
export function formatDateTime(
  timestamp: string | Date | null | undefined,
  locale: Locale = getActiveLocale(),
): string {
  if (!timestamp) return "—";
  const parsed = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  return Number.isNaN(parsed.getTime()) ? "—" : dateTimeFormatter(locale).format(parsed);
}

/**
 * `Q3 2026`, in both languages.
 *
 * Not locale-dependent, deliberately — see the note at the top of this file. It keeps the
 * signature of a formatter so that no call site has to know it is the exception.
 */
export function formatQuarter(quarter: number, year: number): string {
  return `Q${quarter} ${year}`;
}

/** `yyyy-MM-dd`, and nothing else — anchored so a note beginning with a date is left alone. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * An audit value, formatted the way the rest of the app formats that kind of value.
 *
 * Audit rows store **display strings**, stamped when the change was made, so a rename cannot
 * rewrite history. That is right for names and wrong for dates: an expected-close-date change
 * was stamped as `2026-08-25` and read that way in the feed, which is the one place in the
 * product that showed a raw ISO date to a human. Formatting on the way out keeps the stored
 * value untouched and puts one date format on every surface.
 *
 * Only an exact `yyyy-MM-dd` is converted. Everything else — money, names, stage titles, notes,
 * palette labels — is returned as it was written.
 */
export function formatAuditValue(value: string | null | undefined, locale?: Locale): string | null {
  if (value === null || value === undefined) return null;
  return ISO_DATE.test(value) ? formatDate(value, locale) : value;
}

/**
 * How many **calendar days** ago, in the viewer's own timezone.
 *
 * Days, not 24-hour windows: a sign-in at 23:50 last night is "yesterday" at 00:10 this
 * morning, twenty minutes later, because that is what a reader means by it. Rounded because a
 * daylight-saving day is 23 or 25 hours long and a floor would misplace one day a year.
 *
 * Negative when the timestamp is in the future — a clock skew between the server and this
 * browser — which no relative branch claims, so it falls through to the plain date.
 */
function calendarDaysAgo(then: Date, now: Date): number {
  const midnight = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((midnight(now) - midnight(then)) / 86_400_000);
}

/** Beyond this many days the answer is a date; up to it, the answer is how long ago. */
const RELATIVE_SIGN_IN_DAYS = 3;

/**
 * A sign-in, told relatively while that is the more useful answer.
 *
 * `Today, 10:03` · `Yesterday, 17:42` · `2 days ago` · `3 days ago` · `22 Aug 2026`.
 *
 * The clock survives only for today and yesterday, because that is the span where *when* in
 * the day still distinguishes two sign-ins a reader is comparing. Two days out, "2 days ago"
 * is the whole of what anyone takes from the column, and a time beside it is noise. Past
 * three days the relative form stops helping at all — nobody counts to eleven — so it becomes
 * the same date every other date on the screen is.
 *
 * The words come from the catalogue, so Czech says `Dnes` and `před 2 dny` rather than an
 * English string with a Czech clock in it.
 */
export function formatSignIn(
  timestamp: string | Date | null | undefined,
  locale: Locale = getActiveLocale(),
  now: Date = new Date(),
): string {
  if (!timestamp) return "—";
  const parsed = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  if (Number.isNaN(parsed.getTime())) return "—";

  const t = translator(locale);
  const days = calendarDaysAgo(parsed, now);
  const time = timeFormatter(locale).format(parsed);

  if (days === 0) return t("signIn.today", time);
  if (days === 1) return t("signIn.yesterday", time);
  if (days > 1 && days <= RELATIVE_SIGN_IN_DAYS) return t("signIn.daysAgo", days);
  // The instant's own day in the viewer's timezone, as `formatDateTime` shows it — not
  // `formatDate`, which is for business dates that carry no time and no zone at all.
  return dateFormatter(locale).format(parsed);
}
